import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Chip,
  Avatar,
} from '@mui/material';
import {
  ArrowRight,
  ShieldCheck,
  Zap,
  Building2,
  Globe,
  MessageSquare,
  Calendar,
  Flame,
  CheckCircle2,
  Bell,
  Search,
  Wifi,
  Battery,
} from 'lucide-react';

// --- Ultra-Sleek Mobile Frame Device App Mockup (Acolead Mobile CRM & Automation) ---
const MobileAppMockup = () => {
  const [activeTab, setActiveTab] = useState('hot');

  // Parallax subtle tilt effect
  const phoneRef = useRef(null);
  const handleMouseMove = (e) => {
    if (!phoneRef.current) return;
    const rect = phoneRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    const tiltX = (y / (rect.height / 2)) * -5;
    const tiltY = (x / (rect.width / 2)) * 5;
    phoneRef.current.style.transform = `perspective(1000px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) translateY(-6px)`;
  };

  const handleMouseLeave = () => {
    if (!phoneRef.current) return;
    phoneRef.current.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) translateY(0px)';
  };

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: { xs: 2, md: 4 },
      }}
    >
      {/* --- SMARTPHONE FRAME CONTAINER --- */}
      <Box
        ref={phoneRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        sx={{
          position: 'relative',
          width: { xs: 290, sm: 320, md: 335 },
          height: { xs: 580, sm: 620, md: 635 },
          borderRadius: '46px',
          backgroundColor: '#0F172A',
          p: '11px',
          boxShadow: `
            0 0 0 3px #334155,
            0 0 0 6px #1E293B,
            0 25px 60px -12px rgba(15, 23, 42, 0.35),
            0 12px 30px -6px rgba(37, 99, 235, 0.15)
          `,
          transition: 'transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.25s ease',
          '&:hover': {
            boxShadow: `
              0 0 0 3px #475569,
              0 0 0 6px #1E293B,
              0 32px 70px -15px rgba(15, 23, 42, 0.45),
              0 16px 40px -8px rgba(37, 99, 235, 0.25)
            `,
          },
        }}
      >
        {/* Smartphone Screen Glass */}
        <Box
          sx={{
            width: '100%',
            height: '100%',
            backgroundColor: '#F8FAFC',
            borderRadius: '36px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}
        >
          {/* Top Status Bar & Dynamic Island */}
          <Box
            sx={{
              pt: 1.2,
              pb: 0.8,
              px: 2.5,
              backgroundColor: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'relative',
              zIndex: 5,
            }}
          >
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#0F172A' }}>
              9:41
            </Typography>

            {/* Dynamic Island Notch */}
            <Box
              sx={{
                width: 90,
                height: 20,
                backgroundColor: '#000000',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 1,
              }}
            >
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#1E293B' }} />
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#0284C7', opacity: 0.8 }} />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
              <Wifi size={13} color="#0F172A" />
              <Battery size={14} color="#0F172A" />
            </Box>
          </Box>

          {/* App Header Bar */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              backgroundColor: '#FFFFFF',
              borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '10px',
                  backgroundColor: '#2563EB',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#FFFFFF',
                  fontWeight: 800,
                  fontSize: '0.88rem',
                  boxShadow: '0 4px 10px rgba(37, 99, 235, 0.3)',
                }}
              >
                A
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.84rem', fontWeight: 800, color: '#0F172A', lineHeight: 1.1 }}>
                  Acolead CRM
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#10B981' }} />
                  <Typography sx={{ fontSize: '0.65rem', color: '#059669', fontWeight: 700 }}>
                    Live Auto-Sync
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '8px',
                  backgroundColor: '#F1F5F9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Search size={14} color="#64748B" />
              </Box>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '8px',
                  backgroundColor: 'rgba(37, 99, 235, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                <Bell size={14} color="#2563EB" />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    backgroundColor: '#EF4444',
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Quick Metrics Strip */}
          <Box
            sx={{
              px: 2,
              py: 1.2,
              backgroundColor: '#FFFFFF',
              borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography sx={{ fontSize: '0.65rem', color: '#64748B', fontWeight: 600 }}>
                Today's Inflow
              </Typography>
              <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#0F172A' }}>
                48 Leads
              </Typography>
            </Box>
            <Box sx={{ height: 20, width: 1, backgroundColor: '#E2E8F0' }} />
            <Box>
              <Typography sx={{ fontSize: '0.65rem', color: '#64748B', fontWeight: 600 }}>
                Response Time
              </Typography>
              <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#10B981' }}>
                &lt; 28s
              </Typography>
            </Box>
            <Box sx={{ height: 20, width: 1, backgroundColor: '#E2E8F0' }} />
            <Box>
              <Typography sx={{ fontSize: '0.65rem', color: '#64748B', fontWeight: 600 }}>
                Conversion
              </Typography>
              <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#2563EB' }}>
                +310%
              </Typography>
            </Box>
          </Box>

          {/* App Category Filters */}
          <Box
            sx={{
              px: 1.5,
              py: 1,
              display: 'flex',
              gap: 0.8,
              backgroundColor: '#F8FAFC',
            }}
          >
            {[
              { id: 'hot', label: 'Hot Leads (12)', icon: <Flame size={12} /> },
              { id: 'visits', label: 'Site Visits (8)', icon: <Calendar size={12} /> },
              { id: 'whatsapp', label: 'WhatsApp AI', icon: <MessageSquare size={12} /> },
            ].map((tab) => (
              <Box
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                sx={{
                  flex: 1,
                  py: 0.6,
                  px: 0.8,
                  borderRadius: '10px',
                  backgroundColor: activeTab === tab.id ? '#2563EB' : '#FFFFFF',
                  color: activeTab === tab.id ? '#FFFFFF' : '#64748B',
                  border: activeTab === tab.id ? '1px solid #2563EB' : '1px solid rgba(226, 232, 240, 0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.6,
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: activeTab === tab.id ? '0 2px 8px rgba(37, 99, 235, 0.25)' : 'none',
                }}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </Box>
            ))}
          </Box>

          {/* Live Mobile Lead Inflow Feed */}
          <Box
            sx={{
              flex: 1,
              px: 1.5,
              py: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.2,
              overflowY: 'hidden',
            }}
          >
            {/* Lead Card 1 */}
            <Box
              sx={{
                p: 1.4,
                borderRadius: '14px',
                backgroundColor: '#FFFFFF',
                border: '1px solid rgba(37, 99, 235, 0.22)',
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.06)',
                position: 'relative',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.8 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ width: 28, height: 28, fontSize: '0.72rem', bgcolor: '#2563EB', fontWeight: 700 }}>
                    RS
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#0F172A', lineHeight: 1.1 }}>
                      Rahul Sharma
                    </Typography>
                    <Typography sx={{ fontSize: '0.66rem', color: '#64748B' }}>
                      Meta Ad · 3 BHK Luxury
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label="🔥 98% Match"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 800,
                    backgroundColor: 'rgba(239, 68, 68, 0.08)',
                    color: '#DC2626',
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                  }}
                />
              </Box>

              <Box
                sx={{
                  p: 0.8,
                  borderRadius: '8px',
                  backgroundColor: 'rgba(16, 185, 129, 0.06)',
                  border: '1px solid rgba(16, 185, 129, 0.18)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  <MessageSquare size={13} color="#10B981" />
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#065F46' }}>
                    Brochure & Pricing Sent via WhatsApp
                  </Typography>
                </Box>
                <CheckCircle2 size={13} color="#10B981" />
              </Box>
            </Box>

            {/* Lead Card 2 */}
            <Box
              sx={{
                p: 1.4,
                borderRadius: '14px',
                backgroundColor: '#FFFFFF',
                border: '1px solid rgba(226, 232, 240, 0.9)',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.02)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.8 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ width: 28, height: 28, fontSize: '0.72rem', bgcolor: '#8B5CF6', fontWeight: 700 }}>
                    PP
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#0F172A', lineHeight: 1.1 }}>
                      Priya Patel
                    </Typography>
                    <Typography sx={{ fontSize: '0.66rem', color: '#64748B' }}>
                      Channel Partner: PropX
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label="Site Visit"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    backgroundColor: 'rgba(37, 99, 235, 0.08)',
                    color: '#2563EB',
                  }}
                />
              </Box>

              <Box
                sx={{
                  p: 0.8,
                  borderRadius: '8px',
                  backgroundColor: '#F8FAFC',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  <Calendar size={13} color="#2563EB" />
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#334155' }}>
                    Scheduled: Tomorrow at 4:00 PM
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#2563EB' }}>
                  Tower B
                </Typography>
              </Box>
            </Box>

            {/* Lead Card 3 */}
            <Box
              sx={{
                p: 1.4,
                borderRadius: '14px',
                backgroundColor: '#FFFFFF',
                border: '1px solid rgba(226, 232, 240, 0.9)',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.02)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ width: 28, height: 28, fontSize: '0.72rem', bgcolor: '#0D9488', fontWeight: 700 }}>
                    AV
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#0F172A', lineHeight: 1.1 }}>
                      Amit Verma
                    </Typography>
                    <Typography sx={{ fontSize: '0.66rem', color: '#64748B' }}>
                      Auto-Assigned · Sales Exec 1
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#94A3B8' }}>
                  2m ago
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Bottom Floating Action Bar / Mobile Nav */}
          <Box
            sx={{
              p: 1.2,
              px: 2,
              backgroundColor: '#FFFFFF',
              borderTop: '1px solid rgba(226, 232, 240, 0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box
              sx={{
                flex: 1,
                py: 1,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.8,
                boxShadow: '0 4px 14px rgba(37, 99, 235, 0.35)',
                cursor: 'pointer',
              }}
            >
              <MessageSquare size={14} />
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
                1-Click WhatsApp Broadcast
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// --- Hero Section with Mobile Frame UI ---
export const WovenLightHero = () => {
  const navigate = useNavigate();

  const handleScrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        width: '100%',
        pt: { xs: 0, sm: 9, md: 11 },
        pb: { xs: 4, sm: 7, md: 13 },
        minHeight: { xs: 'clamp(700px, 190vw, 820px)', md: 'auto' },
        backgroundColor: '#061A31',
        backgroundImage: { md: 'url("/images/Artboard 2.png")' },
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
        overflow: 'hidden',
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          px: { xs: 0, sm: 4, md: 3, lg: 4 },
        }}
      >
        <Box
          component="img"
          src="/images/Artboard%201%20mobile.png"
          alt="Coalitionify mobile product preview"
          sx={{
            display: { xs: 'block', md: 'none' },
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: 'auto',
            zIndex: 0,
          }}
        />
        <Grid
          container
          spacing={{ xs: 0, lg: 6 }}
          alignItems="center"
          sx={{
            display: { xs: 'flex', md: 'flex' },
            position: 'relative',
            zIndex: 1,
            width: '100%',
            maxWidth: { xs: 520, md: 'none' },
            mx: { xs: 'auto', md: 0 },
            boxSizing: 'border-box',
            px: { xs: 2.25, sm: 3.5, md: 0 },
            pt: {
  xs: 'clamp(320px, 75vw, 400px)',
  sm: 'clamp(350px, 68vw, 430px)',
  md: 0
},
          }}
        >
          {/* Left Column: World-Class Typography & Narrative */}
          <Grid item xs={12} md={6}>

            {/* Hero Eyebrow */}
            <Typography
              variant="overline"
              sx={{
                display: 'block',
                color: '#BFDBFE',
                fontWeight: 800,
                letterSpacing: '0.12em',
                mb: { xs: 1.2, md: 1.8 },
                fontSize: { xs: '0.52rem', md: '0.75rem' },
              }}
            >
              TECHNOLOGY FOR REAL ESTATE
            </Typography>

            {/* Main Headline */}
<Typography
  variant="h1"
  sx={{
    fontWeight: 800,
    fontSize: { xs: 'clamp(1.8rem, 8.2vw, 2.15rem)', sm: 'clamp(2.2rem, 6vw, 3rem)', md: '3.95rem' },
    lineHeight: 1.12,
    letterSpacing: '-0.035em',
    color: '#FFFFFF',
    mb: { xs: 1.8, md: 3 },
  }}
>
  <Box component="span" sx={{ display: 'block' }}>Building Smarter</Box>
  <Box component="span" sx={{ display: 'block' }}>Technology for</Box>
  <Box
    component="span"
    sx={{
      display: 'block',
      background: 'linear-gradient(135deg, #60A5FA)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    }}
  >
    Modern Real Estate
  </Box>
  <Box
    component="span"
    sx={{
      display: 'block',
      background: 'linear-gradient(135deg, #60A5FA)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    }}
  >
    Businesses
  </Box>
</Typography>
            {/* Primary Supporting Message */}
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: 'clamp(0.92rem, 4.1vw, 1.05rem)', sm: '1.08rem', md: '1.22rem' },
                color: 'rgba(255, 255, 255, 0.9)',
                lineHeight: 1.5,
                mb: { xs: 1.6, md: 2.2 },
                fontWeight: 500,
                maxWidth: { xs: '100%', md: 640 },
              }}
            >
              We build technology products and digital solutions that help real estate businesses grow, operate efficiently, and stay ahead.
            </Typography>

            {/* CTA Action Buttons */}
            <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 }, flexWrap: 'wrap', mb: { xs: 3, md: 5 } }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => handleScrollToSection('solutions-section')}
                endIcon={<ArrowRight size={18} />}
                sx={{
                  px: { xs: 1.8, md: 4.2 },
                  py: { xs: 1.05, md: 1.65 },
                  fontSize: { xs: 'clamp(0.76rem, 3.4vw, 0.88rem)', md: '1.02rem' },
                  borderRadius: '12px',
                  fontWeight: 700,
                  letterSpacing: '0.01em',
                  background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                  boxShadow: '0 12px 28px -6px rgba(37, 99, 235, 0.45), 0 4px 12px rgba(37, 99, 235, 0.2)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)',
                    boxShadow: '0 16px 36px -4px rgba(37, 99, 235, 0.65)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
                }}
              >
                Explore Our Solutions
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => window.open('https://wa.me/919112614174', '_blank', 'noopener,noreferrer')}
                sx={{
                  px: { xs: 1.8, md: 4 },
                  py: { xs: 1.05, md: 1.65 },
                  fontSize: { xs: 'clamp(0.76rem, 3.4vw, 0.88rem)', md: '1.02rem' },
                  borderRadius: '12px',
                  fontWeight: 600,
                  borderColor: 'rgba(255, 255, 255, 0.7)',
                  color: '#FFFFFF',
                  backgroundColor: 'rgba(7, 13, 25, 0.28)',
                  backdropFilter: 'blur(8px)',
                  '&:hover': {
                    borderColor: '#FFFFFF',
                    backgroundColor: 'rgba(7, 13, 25, 0.5)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 22px -4px rgba(15, 23, 42, 0.1)',
                  },
                  transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
                }}
              >
                Talk to Us
              </Button>
            </Box>

            {/* Enterprise Trust Badges Grid */}
            <Box
              sx={{
                display: { xs: 'none', md: 'grid' },
                pt: { xs: 2, md: 3 },
                borderTop: '1px solid rgba(255, 255, 255, 0.22)',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
                gap: 2,
              }}
            >
              {[
                { icon: <ShieldCheck size={16} style={{ color: '#2563EB' }} />, label: 'Enterprise Grade' },
                { icon: <Zap size={16} style={{ color: '#10B981' }} />, label: 'AI Workflows' },
                { icon: <Building2 size={16} style={{ color: '#4F46E5' }} />, label: 'Acolead CRM' },
                { icon: <Globe size={16} style={{ color: '#06B6D4' }} />, label: 'Connected API' },
              ].map((badge, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: 1.8,
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid rgba(15, 23, 42, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.03)',
                    }}
                  >
                    {badge.icon}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.82rem', whiteSpace: 'nowrap' }}
                  >
                    {badge.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
};

export default WovenLightHero;
